import React, { useEffect, useMemo, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { MdAddShoppingCart, MdLayers, MdVisibility, MdCheckCircle, MdShoppingBag } from "react-icons/md";
import { getPublicProductBundles, priceProductBundle, addProductBundleToCart, getProductBundleCheckoutLines } from "../../auth/apiProductBundles";
import { isAuthenticated } from "../../auth";
import { taxProductAreaByState } from "../../auth/apiProduct";
import { getProductPrimaryImage } from "../../auth/elakay-utils";
import { toImageUrl } from "../../../config";
import { elakayAutoText, laguangeElakay } from "../dataElakay";
import Checkout from "../CheckoutProd";
import imgProductLakay from "../../Layout/imgs/md3.jpg";
import ElakayImage from "../../../components/images/ElakayImage";
import "./ProductBundles.css";

export const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
export const productThumb = (product) => toImageUrl(getProductPrimaryImage(product || {})) || imgProductLakay;

const T = elakayAutoText;
const SELECT_ALL_TEXT = () => T("h_bnd_select_all2", "Select the options for every product to continue.");
const REASON_TEXT = {
  VARIANT_OUT_OF_STOCK: () => T("h_bnd_r_varstock", "This option is out of stock."),
  VARIANT_UNAVAILABLE: () => T("h_bnd_r_varunav", "This option is no longer available."),
  OUT_OF_STOCK: () => T("h_bnd_r_stock", "Out of stock."),
};
const reasonText = (code) => (REASON_TEXT[code] ? REASON_TEXT[code]() : T("h_bnd_r_generic", "Not available with the selected options."));

// Purchase errors, by the server's error codes. The server message is kept for
// codes whose text carries data (e.g. the new price).
const ERROR_TEXT = {
  VARIANT_REQUIRED: SELECT_ALL_TEXT,
  VARIANT_OUT_OF_STOCK: () => T("h_bnd_e_varstock", "One of the selected options is out of stock."),
  VARIANT_UNAVAILABLE: () => T("h_bnd_e_varunav", "One of the selected options is no longer available."),
  OUT_OF_STOCK: () => T("h_bnd_e_stock", "A product in this offer is out of stock."),
  PRODUCT_NOT_ACTIVE: () => T("h_bnd_e_inactive", "A product in this offer is no longer for sale."),
  PRODUCT_DELETED: () => T("h_bnd_e_deleted", "A product in this offer was removed."),
  PRODUCT_NOT_FOUND: () => T("h_bnd_e_deleted", "A product in this offer was removed."),
  PRODUCT_INCOMPLETE: () => T("h_bnd_e_incomplete", "A product in this offer is temporarily unavailable."),
  BUNDLE_UNAVAILABLE: () => T("h_bnd_e_unavailable", "This offer is no longer available."),
  CART_FULL: () => T("h_bnd_e_cartfull", "Your cart is full. Remove some items to add this offer."),
  NETWORK_ERROR: () => T("h_bnd_e_network", "Network error. Please try again."),
  // The API's catch-all 404 ({ error: "Not found" }): e.g. a server not yet
  // restarted with this route. Never show the bare technical message.
  HTTP_404: () => T("h_bnd_e_404", "Checkout for this offer is temporarily unavailable. Please refresh the page and try again."),
};
export const purchaseErrorText = (r, fallback, bundle) => {
  const base = (r?.code === "PRICE_CHANGED" || r?.code === "LOCAL_SELLING_INELIGIBLE")
    ? r.error : (ERROR_TEXT[r?.code] ? ERROR_TEXT[r.code]() : (r?.error && r.error !== "Not found" ? r.error : fallback));
  // Name the affected product when the server identifies it.
  const line = r?.details?.productId && bundle?.lines?.find((l) => l.productId === r.details.productId);
  return line?.product?.name ? `${line.product.name}: ${base}` : base;
};

// ── Filter normalization ─────────────────────────────────────────────────
// Customer-facing rule for Combined Offer products: a filter is a selector
// only when it has 2+ valid values among the purchasable variants. A filter
// with exactly one value is not shown but is never lost: it stays in the
// chosen variant row, which is what the cart/checkout carry.
const validValue = (v) => v !== null && v !== undefined && String(v).trim() !== "";
export const normalizeVariantFilters = (variants = []) => {
  const values = new Map();
  variants.forEach((row) => Object.entries(row?.filter || {}).forEach(([key, value]) => {
    if (key.startsWith("__") || !validValue(value)) return;
    if (!values.has(key)) values.set(key, new Map());
    const label = String(value).trim();
    const dedupe = label.toLocaleLowerCase();
    if (!values.get(key).has(dedupe)) values.get(key).set(dedupe, label);
  }));
  const visible = [];
  const fixed = {};
  values.forEach((map, key) => {
    const list = [...map.values()];
    if (list.length >= 2) visible.push({ key, values: list });
    else if (list.length === 1) fixed[key] = list[0];
  });
  return { visible, fixed };
};
const same = (a, b) => String(a ?? "").trim().toLocaleLowerCase() === String(b ?? "").trim().toLocaleLowerCase();

// Per-product option picker: independent state per product, impossible
// combinations disabled from the product's own variant rows.
export const VariantPicker = ({ variants, value, onChange, idPrefix }) => {
  const { visible } = useMemo(() => normalizeVariantFilters(variants), [variants]);
  const selectedRow = variants.find((v) => v._id === value);
  const pick0 = (row) => Object.fromEntries(visible.map(({ key }) => [key, row?.filter?.[key]]).filter(([, v]) => validValue(v)));
  const [partial, setPartial] = useState(pick0(selectedRow));
  useEffect(() => { if (selectedRow) setPartial(pick0(selectedRow)); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!visible.length) {
    // Several purchasable rows but no distinguishing filter: plain choice.
    return (
      <select className="pb-select" value={value || ""} onChange={(e) => onChange(e.target.value || null)} aria-label={T("h_bnd_option", "Option")}>
        <option value="">{T("h_bnd_choose_option", "Choose an option")}</option>
        {variants.map((v, i) => <option key={v._id} value={v._id}>{T("h_bnd_option_n", "Option {n}", { n: i + 1 })}</option>)}
      </select>
    );
  }
  const matches = (row, sel) => Object.entries(sel).every(([k, val]) => val === undefined || same(row.filter?.[k], val));
  const possible = (key, val) => variants.some((row) => matches(row, { ...partial, [key]: val }));
  const pick = (key, val) => {
    let next = { ...partial, [key]: val };
    if (!variants.some((row) => matches(row, next))) next = { [key]: val };
    setPartial(next);
    const complete = visible.every(({ key: k }) => next[k] !== undefined);
    const exact = complete ? variants.find((row) => matches(row, next)) : null;
    onChange(exact ? exact._id : null);
  };
  return (
    <div className="pb-variants">
      {visible.map(({ key, values }) => (
        <div className="pb-variant-group" key={key} role="group" aria-labelledby={`${idPrefix}-${key}`}>
          <span className="pb-variant-label" id={`${idPrefix}-${key}`}>{key}</span>
          <div className="pb-variant-chips">
            {values.map((val) => {
              const on = partial[key] !== undefined && same(partial[key], val);
              return (
                <button type="button" key={val} className={`pb-chip${on ? " pb-chip--on" : ""}`}
                  disabled={!possible(key, val)} aria-pressed={on} onClick={() => pick(key, val)}>{val}</button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

const BundleImages = ({ bundle, compact = false }) => {
  if (!compact && bundle.promotionalImages?.length) {
    return <div className="pb-offer-promo"><ElakayImage preset="product-medium" src={toImageUrl(bundle.promotionalImages[0])} alt={bundle.name} /></div>;
  }
  return (
    <div className="pb-offer-thumbs">
      {bundle.lines.map((l, i) => (
        <React.Fragment key={l.productId}>
          {i > 0 && <span className="pb-offer-plus" aria-hidden="true">+</span>}
          <ElakayImage preset="product-thumb" fallback={imgProductLakay} src={productThumb(l.product)} alt={l.product?.name || ""} title={l.product?.name || ""} />
        </React.Fragment>
      ))}
    </div>
  );
};

export const BundlePriceSummary = ({ totals }) => (
  <div className="pb-offer-prices">
    {totals.hasDiscount ? (
      <>
        <div className="pb-offer-price-row"><span>{T("h_bnd_original_total", "Original total")}</span><span className="pb-strike">{money(totals.originalTotal)}</span></div>
        <div className="pb-offer-price-row pb-offer-price-main"><span>{T("h_bnd_bundle_price", "Bundle price")}</span><strong>{money(totals.finalTotal)}</strong></div>
        <div className="pb-offer-save">{T("h_bnd_you_save", "You save")} {money(totals.savings)}{totals.savingsPercent > 0 ? ` (${totals.savingsPercent}%)` : ""}</div>
      </>
    ) : (
      // No discount: the combined total, without suggesting a reduction.
      <div className="pb-offer-price-row pb-offer-price-main"><span>{T("h_bnd_combined_total", "Combined total")}</span><strong>{money(totals.finalTotal)}</strong></div>
    )}
  </div>
);

// The two purchase actions, identical on the card and in the modal. The
// amount always comes from the same server totals object. iconOnly: compact
// card version (label kept as aria-label + tooltip for accessibility).
const PurchaseButtons = ({ onBuy, onAdd, busy, disabled, amount, iconOnly = false }) => {
  const buyLabel = T("h_bnd_buy_now", "Buy Now");
  const addLabel = T("h_bnd_add_cart", "Add Bundle to Cart");
  const withAmount = (label) => (amount !== undefined ? `${label} · ${money(amount)}` : label);
  return (
    <div className={iconOnly ? "pb-icon-row-part" : "pb-buy-row"}>
      <button type="button" className={`pb-btn pb-btn--primary${iconOnly ? " pb-btn--icon" : ""}`} onClick={onBuy} disabled={disabled || !onBuy || Boolean(busy)}
        aria-label={withAmount(buyLabel)} title={buyLabel} aria-busy={busy === "buy"}>
        <MdShoppingBag aria-hidden="true" />{!iconOnly && <span>{busy === "buy" ? T("h_bnd_preparing_checkout", "Preparing checkout...") : withAmount(buyLabel)}</span>}
      </button>
      <button type="button" className={`pb-btn pb-btn--cart${iconOnly ? " pb-btn--icon" : ""}`} onClick={onAdd} disabled={disabled || !onAdd || Boolean(busy)}
        aria-label={withAmount(addLabel)} title={addLabel} aria-busy={busy === "add"}>
        <MdAddShoppingCart aria-hidden="true" />{!iconOnly && <span>{busy === "add" ? T("h_bnd_adding", "Adding...") : withAmount(addLabel)}</span>}
      </button>
    </div>
  );
};

// Presentational card, shared by Product View and the Seller's preview.
export const BundleOfferCard = ({ bundle, onView, onBuy, onAdd, busy = null, preview = false }) => (
  <div className="pb-offer-card">
    <div className="pb-offer-eyebrow"><MdLayers /> {T("h_bnd_eyebrow", "Elakay combined offer")}</div>
    <h3 className="pb-offer-title">{bundle.name}</h3>
    {bundle.description && <p className="pb-offer-desc">{bundle.description}</p>}
    <BundleImages bundle={bundle} />
    {bundle.promotionalImages?.length > 0 && <BundleImages bundle={bundle} compact />}
    <div className="pb-offer-count">{T("h_bnd_count", "{n} products included", { n: bundle.totals.productCount })}</div>
    <BundlePriceSummary totals={bundle.totals} />
    <div className="pb-offer-actions pb-offer-actions--icons">
      <button type="button" className="pb-btn pb-btn--ghost pb-btn--icon" onClick={onView} disabled={!onView}
        aria-label={T("h_bnd_view_offer", "View Complete Offer")} title={T("h_bnd_view_offer", "View Complete Offer")}>
        <MdVisibility aria-hidden="true" />
      </button>
      <PurchaseButtons onBuy={onBuy} onAdd={onAdd} busy={busy} disabled={preview} iconOnly />
    </div>
  </div>
);

export const BundleDetailsModal = ({ show, onHide, bundle: initial, onBuy, onAdd, preview = false }) => {
  const [bundle, setBundle] = useState(initial);
  const [selections, setSelections] = useState({});
  const [pricing, setPricing] = useState(false);
  const [busy, setBusy] = useState(null);
  const [message, setMessage] = useState(null);
  const abortRef = useRef(null);

  useEffect(() => { setBundle(initial); setSelections({}); setMessage(null); }, [initial?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const reprice = (next) => {
    if (preview || !initial?._id) return;
    // Re-price and re-validate the exact selection on the server.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setPricing(true);
    priceProductBundle(initial._id, next, controller.signal).then((r) => {
      if (r?.success && r.bundle) setBundle(r.bundle);
      else if (r && !r.success) setMessage({ type: "error", text: purchaseErrorText(r, T("h_bnd_e_generic", elakayAutoText("h_9a3ea0598e7f4b", "Something went wrong. Please try again."))) });
    }).catch(() => {}).finally(() => { if (abortRef.current === controller) setPricing(false); });
  };
  const choose = (productId, rowId) => {
    const next = { ...selections };
    if (rowId) next[productId] = rowId; else delete next[productId];
    setSelections(next);
    setMessage(null);
    reprice(next);
  };

  if (!bundle) return null;
  const missing = bundle.lines.filter((l) => l.variantChoice && !selections[l.productId]);
  const unavailable = bundle.lines.filter((l) => !l.available);
  const blocked = preview || missing.length > 0 || unavailable.length > 0 || pricing || bundle.purchasable === false;

  const run = async (kind, handler) => {
    if (!handler) return;
    setBusy(kind);
    setMessage(null);
    const r = await handler(bundle, selections);
    setBusy(null);
    if (r?.message) setMessage({ type: r.ok ? "success" : "error", text: r.message });
    if (r?.code === "PRICE_CHANGED") reprice(selections);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered contentClassName="pb-modal" aria-labelledby="pb-details-title">
      <Modal.Header closeButton>
        <Modal.Title id="pb-details-title" className="pb-modal-title"><MdLayers /> {bundle.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {bundle.description && <p className="pb-offer-desc">{bundle.description}</p>}
        {bundle.promotionalImages?.length > 0 && (
          <div className="pb-modal-gallery">
            {bundle.promotionalImages.map((url) => <ElakayImage key={url} preset="product-medium" src={toImageUrl(url)} alt={bundle.name} />)}
          </div>
        )}
        <ul className="pb-modal-lines">
          {bundle.lines.map((l) => (
            <li key={l.productId} className={`pb-modal-line${l.available ? "" : " pb-modal-line--off"}`}>
              <ElakayImage preset="product-thumb" fallback={imgProductLakay} src={productThumb(l.product)} alt={l.product?.name || ""} />
              <div className="pb-modal-line-body">
                <div className="pb-modal-line-head">
                  <strong>{l.product?.name}</strong>
                  {l.quantity > 1 && <span className="pb-qty">× {l.quantity}</span>}
                </div>
                <div className="pb-modal-shop">{T("h_bnd_sold_by", "Sold by")} {l.shopName}</div>
                {l.product?.description && <p className="pb-modal-line-desc">{String(l.product.description).replace(/<[^>]+>/g, " ").slice(0, 180)}</p>}
                <div className="pb-modal-line-price">
                  {l.unitDiscount > 0 && <span className="pb-strike">{money(l.regularUnitPrice)}</span>}
                  <strong>{money(l.finalUnitPrice)}</strong>
                  {l.unitDiscount > 0 && <span className="pb-badge pb-badge--save">-{money(l.unitDiscount)}</span>}
                </div>
                {l.variantChoice && l.variants?.length > 0 && (
                  <VariantPicker variants={l.variants} value={selections[l.productId] || null} onChange={(rowId) => choose(l.productId, rowId)} idPrefix={`pb-${bundle._id}-${l.productId}`} />
                )}
                {!l.available && <div className="pb-line-issue">{reasonText(l.unavailableReason)}</div>}
              </div>
            </li>
          ))}
        </ul>
        <BundlePriceSummary totals={bundle.totals} />
        {missing.length > 0 && !preview && <div className="pb-hint" role="status">{SELECT_ALL_TEXT()}</div>}
        {message && <div className={`pb-alert pb-alert--${message.type}`} role={message.type === "error" ? "alert" : "status"}>{message.type === "success" && <MdCheckCircle />} {message.text}</div>}
      </Modal.Body>
      <Modal.Footer className="pb-modal-footer">
        <button type="button" className="pb-btn pb-btn--ghost" onClick={onHide}>{T("h_bnd_close", "Close")}</button>
        <PurchaseButtons onBuy={onBuy ? () => run("buy", onBuy) : null} onAdd={onAdd ? () => run("add", onAdd) : null}
          busy={busy} disabled={blocked} amount={pricing ? undefined : bundle.totals.finalTotal} />
      </Modal.Footer>
    </Modal>
  );
};

// Product View entry point: the real offers, loaded once per product, and the
// two purchase paths. Buy Now hands server-built lines to the SAME Checkout
// component Product View already uses (cart mode for several lines).
const ProductBundleOffer = ({ productId, infoSource, Saddress, setSAddressFinal, onOrderSuccess }) => {
  const [bundles, setBundles] = useState([]);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(null); // { id, kind }
  const [notice, setNotice] = useState(null);
  const [checkout, setCheckout] = useState(null);

  useEffect(() => {
    let alive = true;
    setBundles([]);
    setCheckout(null);
    if (!productId) return undefined;
    getPublicProductBundles(productId).then((r) => { if (alive && r?.success) setBundles(r.bundles || []); });
    return () => { alive = false; };
  }, [productId]);

  const tokenOrSignin = () => {
    const { token } = isAuthenticated() || {};
    if (!token) window.location.href = "/signin";
    return token;
  };
  const refreshCart = (token) => { if (token && typeof infoSource?.cartItems === "function") infoSource.cartItems(token); };

  const addToCart = async (bundle, selections) => {
    const token = tokenOrSignin();
    if (!token) return { ok: false };
    const r = await addProductBundleToCart(token, bundle._id, selections);
    if (!r?.success) return { ok: false, code: r?.code, message: purchaseErrorText(r, T("h_bnd_add_failed", "The offer could not be added to your cart."), bundle) };
    refreshCart(token);
    return { ok: true, message: r.merged ? T("h_bnd_added_more", "One more complete offer was added to your cart.") : T("h_bnd_added", "The complete offer was added to your cart.") };
  };

  const buyInFlight = useRef(false);
  const buyNow = async (bundle, selections) => {
    // One preparation at a time: a double-click never opens two checkouts.
    if (buyInFlight.current) return { ok: false };
    const token = tokenOrSignin();
    if (!token) return { ok: false };
    buyInFlight.current = true;
    try {
      return await prepareBuyNow(token, bundle, selections);
    } finally {
      buyInFlight.current = false;
    }
  };
  const prepareBuyNow = async (token, bundle, selections) => {
    const r = await getProductBundleCheckoutLines(token, bundle._id, selections, bundle.totals.finalTotal);
    if (!r?.success) {
      if (r?.code === "PRICE_CHANGED" && r.details?.totals) setBundles((cur) => cur.map((b) => (b._id === bundle._id ? { ...b, totals: r.details.totals } : b)));
      return { ok: false, code: r?.code, message: purchaseErrorText(r, T("h_bnd_buy_failed", "The offer could not be prepared for checkout."), bundle) };
    }
    // Same tax source Product View and the Cart use for a product's shop zone.
    const lines = await Promise.all(r.lines.map(async (l) => {
      const buyerDestination = Saddress || {};
      const buyerPostal = buyerDestination.postal_code || buyerDestination.postalCode;
      const destination = buyerDestination.country && buyerPostal ? buyerDestination : (l.addressShop || {});
      const destinationPostal = destination.postal_code || destination.postalCode;
      const destinationRegion = destination.state || destination.State;
      const tax = destination.country && destinationPostal
        ? await (destinationRegion
          ? taxProductAreaByState(destination.country, destinationPostal, destinationRegion)
          : taxProductAreaByState(destination.country, destinationPostal)).catch(() => null)
        : null;
      const offerLine = bundle.lines.find((x) => x.productId === l.productID);
      const shown = normalizeVariantFilters(offerLine?.variants || []).visible.map((f) => f.key);
      const displayFilters = Object.fromEntries(shown.filter((k) => l.filter?.[k] !== undefined).map((k) => [k, l.filter[k]]));
      return { ...l, displayFilters, taxprodZone: tax?.zoneTax || null, paymentIntent: { customer: infoSource?.userInfo?.StripeCustomerId, payment_method: null } };
    }));
    setOpen(null);
    setCheckout({ groupId: r.groupId, name: r.name, totals: r.totals, lines });
    return { ok: true };
  };

  // Card shortcuts: any product with options needs the customer's choice first.
  const quick = async (bundle, kind) => {
    if (bundle.lines.some((l) => l.variantChoice)) { setOpen(bundle); return; }
    setBusy({ id: bundle._id, kind });
    setNotice(null);
    const r = kind === "buy" ? await buyNow(bundle, {}) : await addToCart(bundle, {});
    setBusy(null);
    if (r?.message) setNotice({ id: bundle._id, type: r.ok ? "success" : "error", text: r.message });
  };

  if (!bundles.length) return null;
  return (
    <section className="pb-offers" aria-label={T("h_bnd_eyebrow", "Elakay combined offer")}>
      {bundles.map((b) => (
        <div key={b._id}>
          <BundleOfferCard bundle={b} onView={() => setOpen(b)} onBuy={() => quick(b, "buy")} onAdd={() => quick(b, "add")}
            busy={busy?.id === b._id ? busy.kind : null} />
          {notice?.id === b._id && <div className={`pb-alert pb-alert--${notice.type}`} role={notice.type === "error" ? "alert" : "status"}>{notice.text}</div>}
        </div>
      ))}
      {open && <BundleDetailsModal show={Boolean(open)} bundle={open} onHide={() => setOpen(null)} onBuy={buyNow} onAdd={addToCart} />}
      {checkout && (
        // The existing Checkout, rendering only its own checkout modal.
        <Checkout
          key={checkout.groupId}
          modalOnly
          onClose={() => setCheckout(null)}
          Saddress={Saddress}
          setSAddressFinal={setSAddressFinal}
          addressSel={infoSource?.address}
          products={checkout.lines}
          cart={true}
          owner={checkout.lines[0]?.owner}
          addressShop={checkout.lines[0]?.addressShop || {}}
          laguangeElakay={laguangeElakay}
          infoSource={infoSource}
          onOrderSuccess={() => { setCheckout(null); onOrderSuccess && onOrderSuccess(); }}
        />
      )}
    </section>
  );
};

export default ProductBundleOffer;
